/**
 * HistoryReportsPage — AUDIT-INCIDENT-HISTORY PRD v1 S4: the unified
 * "History & Reports" surface.
 *
 * Tabs = Audits | Incidents | Reports:
 *  - Audits / Incidents tabs REUSE the existing SafetyAuditsPage /
 *    IncidentHistoryPage components (no forked logic). They stay mounted
 *    while the Reports tab is open so the tables' own CSV exports (and their
 *    "Exports the currently loaded rows" honesty) remain true.
 *  - Reports tab: period picker (this quarter / last quarter / this year /
 *    custom from-to) → GET /api/reports/review → trend cards (counts +
 *    previous + delta) with drill-down links that use ONLY filter params the
 *    target pages actually accept, plus PDF export of the review payload.
 *    Demo handling lives server-side (includeDemo default EXCLUDE) — the
 *    client passes nothing.
 *
 * Role access is SERVER-DECIDED per tab (Sol-1 redesign): tabs render for
 * every authenticated role and each tab's own fetch decides — a 403 renders
 * an explained why+how restriction in place of the data (never a client role
 * guess). D3 REVISED (2026-10-15): the cached-role pre-denial optimization
 * was DELETED — /reports/review is fetched whenever the Reports tab is
 * active and the server decides for EVERY role; the denial panel renders
 * Every number is straight from the API payload; an all-zero period shows
 * ONLY "No data for this period" plus the top-level totals — zero-valued
 * trend rows and 'view rows' drill-down links are suppressed (G5, 2026-10-15).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { History, FileDown, AlertTriangle } from 'lucide-react';
import { api } from '@/utils/api';
import { SafetyAuditsPage } from './SafetyAuditsPage';
import { IncidentHistoryPage } from './IncidentHistoryPage';
import { exportReviewPdf, exportAnnualReviewPdf, reviewPdfFileName } from '@/utils/reviewPdfExport';
import type { ReviewPayload, AnnualReviewPayload } from '@/utils/reviewPayloadTypes';
// S4 design pass §4: date parsing/formatting lives ONLY in utils/utcDay.ts.
// The page re-exports the core so the existing spec import path
// (`from '../HistoryReportsPage'`) keeps working — no component may call
// `new Date(...)` on a date-only value for parsing or display.
import { isValidDateInput, utcDayBounds } from '@/utils/utcDay';
export { isValidDateInput } from '@/utils/utcDay';

type PeriodChoice = 'this-quarter' | 'last-quarter' | 'this-year' | 'custom';

/**
 * Total bounds resolver — NEVER throws for any input. Returns null when a
 * custom range is missing an input or reversed (MED-2): callers must not
 * build a request, and the UI shows a why+how alert instead.
 */
function boundsFor(choice: PeriodChoice, from: string, to: string): { from: string; to: string } | null {
  const now = new Date();
  const y = now.getUTCFullYear();
  const q = Math.floor(now.getUTCMonth() / 3) + 1;
  if (choice === 'this-quarter') return quarterBounds(y, q);
  // H1: in Q1, "last quarter" is Q4 of the PREVIOUS year.
  if (choice === 'last-quarter') return quarterBounds(q === 1 ? y - 1 : y, q === 1 ? 4 : q - 1);
  if (choice === 'this-year') {
    return { from: new Date(Date.UTC(y, 0, 1)).toISOString(), to: new Date(Date.UTC(y + 1, 0, 1) - 1).toISOString() };
  }
  // custom: validate BEFORE constructing anything so an empty/reversed/
  // calendar-invalid range can never throw or silently normalize here
  // (S4 design pass §6: the inline Date construction is replaced by
  // utcDayBounds — the single owner of whole-day UTC bounds).
  if (!isValidDateInput(from) || !isValidDateInput(to) || from > to) return null;
  const b = utcDayBounds(from, to);
  return b ? { from: b.fromIso, to: b.toIso } : null;
}

function quarterBounds(year: number, q: number): { from: string; to: string } {
  return {
    from: new Date(Date.UTC(year, (q - 1) * 3, 1, 0, 0, 0, 0)).toISOString(),
    to: new Date(Date.UTC(year, q * 3, 1, 0, 0, 0, 0) - 1).toISOString(),
  };
}

const stat = (s: { count: number; previous: number; delta: number }) =>
  `${s.count} (previous ${s.previous}, ${s.delta > 0 ? '+' : ''}${s.delta})`;

function DrillLink({ to, children }: { to: string; children: React.ReactNode }) {
  return <Link to={to} className="text-blue-300 hover:text-blue-200 hover:underline">{children}</Link>;
}

function TrendRows({
  rows,
  linkFor,
}: {
  rows: Array<{ key: string; count: number; previous: number; delta: number }>;
  linkFor?: (key: string) => string | null;
}) {
  if (!rows.length) return <p className="text-slate-500 text-sm">No keys recorded for this period.</p>;
  return (
    <ul className="space-y-1 text-sm text-slate-300">
      {rows.map((c) => (
        <li key={c.key}>
          {c.key}: {c.count} (previous {c.previous}, {c.delta > 0 ? '+' : ''}{c.delta})
          {linkFor && linkFor(c.key) && (
            <>
              {' — '}
              <DrillLink to={linkFor(c.key)!}>view rows</DrillLink>
            </>
          )}
        </li>
      ))}
    </ul>
  );
}

export function HistoryReportsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'audits';
  // D-Sol-1a + D3 REVISED: no client role gate — the Reports tab ALWAYS
  // fetches /reports/review when active and the server decides for every
  // role (the guess is never the gate). The denial panel renders only on a
  // server 403; a stale cached role never short-circuits the fetch.
  const [denied, setDenied] = useState(false);
  // Role-neutral denial (Sol r3 fix): the server's own denial message, shown
  // verbatim in the panel so the explanation is grounded in the actual 403
  // body — never a client-side role diagnosis.
  const [deniedMsg, setDeniedMsg] = useState<string | null>(null);

  const [choice, setChoice] = useState<PeriodChoice>('this-quarter');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [payload, setPayload] = useState<ReviewPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [annualLoading, setAnnualLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [annualError, setAnnualError] = useState<string | null>(null);
  const loadGenRef = useRef(0);

  // Load the review payload for the selected period when the Reports tab is active.
  useEffect(() => {
    if (tab !== 'reports') return undefined;
    const gen = ++loadGenRef.current;
    const bounds = boundsFor(choice, customFrom, customTo);
    // Sol-2 (contract F2): an empty/reversed custom range builds no request
    // AND must not leave the previous period's payload visible or a spinner
    // stuck — the prior request's finally is invalidated by the effect
    // re-run, so clear state SYNCHRONOUSLY here. Only the render-body
    // validation alert (customRangeProblem) survives.
    if (!bounds) {
      setLoading(false);
      setPayload(null);
      setError(null);
      return undefined;
    }
    // G1 (Sol closure): clear the prior payload SYNCHRONOUSLY at EVERY new
    // valid request start — valid-to-valid transitions included — so no
    // stale period is ever displayed or exportable while a load is in
    // flight. (D2's clear-to-loading rule, generalized to all transitions.)
    setLoading(true);
    setError(null);
    setPayload(null);
    setDenied(false);
    setDeniedMsg(null);
    api
      .get('/reports/review', { params: { from: bounds.from, to: bounds.to } })
      .then((res: any) => {
        if (gen !== loadGenRef.current) return;
        setPayload(res?.data?.data ?? res?.data ?? null);
        setError(null);
      })
      .catch((e: any) => {
        if (gen !== loadGenRef.current) return;
        if (e?.response?.status === 403) {
          // D3 REVISED + role-neutral (Sol r3): the server denied — ONE
          // role-neutral explained denial replaces the picker/export
          // affordances for EVERY role. The server's own message is
          // incorporated verbatim; no client-side role diagnosis.
          setDenied(true);
          setDeniedMsg(e?.response?.data?.message || e?.response?.data?.error || e?.message || null);
          const msg = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'unknown error';
          setError(`Couldn't load the review report — ${msg}`);
          setPayload(null);
        } else {
          const msg = e?.response?.data?.message || e?.message || 'unknown error';
          setError(`Couldn't load the review report — ${msg}`);
          setPayload(null);
        }
      })
      .finally(() => {
        if (gen === loadGenRef.current) setLoading(false);
      });
    return () => { loadGenRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, choice, customFrom, customTo]);

  // D3 REVISED: the server decides — the Reports tab renders the picker +
  // exports + data only after a 200; a 403 renders an explained denial
  // instead of export affordances the role can never successfully use.

  // H3: children seed their filters only on mount — remount an embedded page
  // when one of ITS drill-down seed params changes in the URL. Only allowed
  // params go into the key, so unrelated param changes (tab switches) never
  // reset in-tab filter edits.
  const auditsSeedKey = ['constructionSupervisor', 'contractorName', 'findingType', 'severity', 'siteName', 'startDateFrom', 'startDateTo', 'towerType']
    .map((p) => `${p}=${searchParams.get(p) ?? ''}`)
    .join('&');
  const incidentsSeedKey = ['dateFrom', 'dateTo', 'investigationStatus', 'q', 'severity', 'type']
    .map((p) => `${p}=${searchParams.get(p) ?? ''}`)
    .join('&');

  const setTab = (t: string) => setSearchParams({ tab: t });
  const bounds = boundsFor(choice, customFrom, customTo);
  // MED-2: why+how copy for a custom range the server would reject.
  const customRangeProblem =
    choice !== 'custom'
      ? null
      : !isValidDateInput(customFrom) || !isValidDateInput(customTo)
        ? 'Choose both a From and a To date to build the report range.'
        : customFrom > customTo
          ? 'From must come on or before To — the server rejects reversed ranges.'
          : null;

  const exportPdf = async () => {
    if (!payload) return;
    setExportError(null);
    try {
      await exportReviewPdf({ payload }, reviewPdfFileName(payload.period.from, payload.period.to));
    } catch (e: any) {
      const msg = e?.message || 'unknown error';
      setExportError(`Couldn't export the period PDF — ${msg}. Check that pop-ups/downloads are allowed for this page, then try again; if it keeps failing, tell your administrator.`);
    }
  };

  const exportAnnualPdf = async () => {
    setAnnualError(null);
    setAnnualLoading(true);
    try {
      const year = new Date().getUTCFullYear();
      const res = await api.get('/reports/review/annual', { params: { year: String(year) } });
      const annual = (res?.data?.data ?? res?.data) as AnnualReviewPayload;
      await exportAnnualReviewPdf({ annual });
    } catch (e: any) {
      const msg = e?.response?.data?.message || e?.message || 'unknown error';
      setAnnualError(`Couldn't generate the annual PDF — the server refused the request (${msg}). Check your role or try again; if it keeps failing, tell your administrator.`);
    } finally {
      setAnnualLoading(false);
    }
  };

  // F3 (Sol-3): drill links carry the payload's AUTHORITATIVE period, so a
  // card computed for the loaded period opens the table on those same rows —
  // never all-time. UTC-day format matches what the tables' own date filters
  // send (SafetyAuditsPage startDateFrom/To, IncidentHistoryPage dateFrom/To).
  const periodDays = payload
    ? { from: payload.period.from.slice(0, 10), to: payload.period.to.slice(0, 10) }
    : null;
  const auditsLink = (extra: Record<string, string>) =>
    `/history?${new URLSearchParams({
      tab: 'audits',
      ...(periodDays ? { startDateFrom: periodDays.from, startDateTo: periodDays.to } : {}),
      ...extra,
    }).toString()}`;
  const incidentsLink = (extra: Record<string, string>) =>
    `/history?${new URLSearchParams({
      tab: 'incidents',
      ...(periodDays ? { dateFrom: periodDays.from, dateTo: periodDays.to } : {}),
      ...extra,
    }).toString()}`;

  const emptyPeriod =
    !!payload && payload.findings.total.count === 0 && payload.incidents.total.count === 0;

  // LIVE-UAT G5: a FULLY empty period (no positive count anywhere — totals and
  // every facet row are zero) must not render zero-valued trend rows,
  // '(previous …' stats or 'view rows' drill-down links: zeros are not
  // measurements and the drill-downs are pointless. A period with totals 0 but
  // nonzero facet rows is NOT empty — its real rows keep rendering.
  const trendHasData = (rows: Array<{ count: number }> | undefined) =>
    !!rows && rows.some((r) => r.count > 0);
  const fullyEmpty =
    !!payload &&
    !trendHasData([payload.findings.total as unknown as { count: number }]) &&
    !trendHasData([payload.incidents.total as unknown as { count: number }]) &&
    payload.incidents.nearMissCount.count === 0 &&
    payload.findings.openActionAging.overdue.count === 0 &&
    !trendHasData(payload.findings.bySeverity) &&
    !trendHasData(payload.findings.byFindingType) &&
    !trendHasData(payload.findings.byStatus) &&
    !trendHasData(payload.findings.topCategories) &&
    !trendHasData(payload.incidents.byType) &&
    !trendHasData(payload.incidents.bySeverity) &&
    !(payload.incidents.facets ?? []).some((f) => trendHasData(f.values)) &&
    !trendHasData(payload.leadingIndicators.auditsByContractor) &&
    !trendHasData(payload.leadingIndicators.auditsBySite) &&
    !trendHasData(payload.leadingIndicators.auditsByRigType) &&
    !trendHasData(payload.leadingIndicators.auditsBySupervisor) &&
    !trendHasData(payload.leadingIndicators.investigationFactorsByCategory);

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header — same Link row pattern as the S1 Safety Audits header */}
      <div className="bg-slate-800/50 border-b border-slate-700">
        <div className="container mx-auto px-6 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <History className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">History &amp; Reports</h1>
              <p className="text-slate-400">Audit &amp; incident history, trends, and review exports</p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Link
              to="/dashboard"
              className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition"
            >
              Dashboard
            </Link>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="container mx-auto px-6 pt-4">
        <div className="flex gap-2">
          {[
            ['audits', 'Audits'],
            ['incidents', 'Incidents'],
            ['reports', 'Reports'],
          ].map(([key, label]) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 rounded-lg transition ${
                tab === key ? 'bg-indigo-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content — Audits/Incidents stay mounted so their CSV exports stay honest.
          H3: seed-param key remounts a child when a drill-down filter lands. */}
      <div className={tab === 'audits' ? '' : 'hidden'}>
        <SafetyAuditsPage key={auditsSeedKey} />
      </div>
      <div className={tab === 'incidents' ? '' : 'hidden'}>
        <IncidentHistoryPage key={incidentsSeedKey} />
      </div>

      {tab === 'reports' && (
        <div className="container mx-auto px-6 py-6">
          {denied ? (
            /* D3 REVISED (server decides): the fetch happened and the server
               answered 403 — an explained denial instead of export
               affordances that can never succeed. The Audits/Incidents tabs
               still let the server decide per tab. */
            <>
            <div role="alert" className="p-4 rounded-lg bg-amber-900/40 border border-amber-700 text-amber-100 text-sm max-w-2xl">
              <div>Access denied — review reports are not available for your account.</div>
              <div className="mt-2">
                Review reports are a management-tier read that requires the company-wide incident view permission,
                so the server rejected this request. To get access, ask an administrator to grant your account
                the company-wide incident view permission, then reload this tab.
              </div>
              {deniedMsg && (
                <div className="mt-2 text-amber-200">Server message: {deniedMsg}</div>
              )}
            </div>
            {/* The honest why+how fetch error still explains the failed
                request even in the denial view (existing spec pins the copy). */}
            {error && (
              <div role="alert" className="mt-4 p-3 rounded-lg bg-red-900/40 text-red-200 text-sm max-w-2xl">
                {error}
              </div>
            )}
          </>
          ):(
          <><div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 mb-6 flex flex-wrap items-end gap-4">
            {/* Period picker */}
            <label className="text-sm text-slate-400">
              Period
              <select
                value={choice}
                onChange={(e) => setChoice(e.target.value as PeriodChoice)}
                className="mt-1 block px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
              >
                <option value="this-quarter">This quarter</option>
                <option value="last-quarter">Last quarter</option>
                <option value="this-year">This year</option>
                <option value="custom">Custom from–to</option>
              </select>
            </label>
            {choice === 'custom' && (
              <>
                <label className="text-sm text-slate-400">
                  From (UTC day)
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => setCustomFrom(e.target.value)}
                    className="mt-1 block px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  />
                </label>
                <label className="text-sm text-slate-400">
                  To (UTC day)
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => setCustomTo(e.target.value)}
                    className="mt-1 block px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm"
                  />
                </label>
              </>
            )}
            <button
              onClick={exportPdf}
              disabled={!payload}
              title="Export the review report for the selected period as a PDF"
              aria-label="Export the review report as a PDF"
              className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-500 transition flex items-center gap-2 disabled:opacity-50"
            >
              <FileDown className="h-4 w-4" />
              Export PDF
            </button>
            {choice === 'this-year' && (
              <button
                onClick={exportAnnualPdf}
                disabled={annualLoading}
                title="Export the annual review (aggregated by quarter) as a PDF"
                className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600 transition disabled:opacity-50"
              >
                {annualLoading ? 'Generating…' : 'Annual PDF (by quarter)'}
              </button>
            )}
          </div>

          {customRangeProblem && (
            <div role="alert" className="mb-4 p-3 rounded-lg bg-amber-900/40 text-amber-200 text-sm">
              {customRangeProblem}
            </div>
          )}
          {exportError && (
            <div role="alert" className="mb-4 p-3 rounded-lg bg-red-900/40 text-red-200 text-sm">
              {exportError}
            </div>
          )}
          {annualError && (
            <div role="alert" className="mb-4 p-3 rounded-lg bg-red-900/40 text-red-200 text-sm">
              {annualError}
            </div>
          )}

          {loading && <p className="text-slate-400 mb-4">Loading review…</p>}
          {error && (
            <div role="alert" className="mb-4 p-3 rounded-lg bg-red-900/40 text-red-200 text-sm">
              {error}
            </div>
          )}

          {emptyPeriod && (
            <div className="mb-4 p-3 rounded-lg bg-slate-800 text-slate-300 text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              No data for this period
            </div>
          )}

          {/* LIVE-UAT G5 fix: a fully empty period renders ONLY the honest
              empty-state line — zero-valued trend rows, '(previous …' stats and
              'view rows' drill-down links are suppressed (zeros are not
              measurements; drill-down affordances are pointless there). The
              top-level Findings/Incidents/Open-actions totals stay visible so
              an empty period is never mistaken for a failed load. Non-empty
              rendering is unchanged. Period picker + PDF export remain usable
              (exporting an empty period is honest). */}
          {payload && !error && !fullyEmpty && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Findings</h3>
                <p className="text-slate-300 text-sm">Total: {stat(payload.findings.total)}</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Incidents</h3>
                <p className="text-slate-300 text-sm">Total: {stat(payload.incidents.total)}</p>
                <p className="text-slate-300 text-sm">Near misses: {stat(payload.incidents.nearMissCount)}</p>
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Open corrective actions</h3>
                <p className="text-slate-300 text-sm">Overdue: {stat(payload.findings.openActionAging.overdue)}</p>
                <p className="text-slate-300 text-sm">Not yet due: {stat(payload.findings.openActionAging.buckets.notDue)}</p>
                <p className="text-slate-300 text-sm">1–30 days overdue: {stat(payload.findings.openActionAging.buckets.days1to30)}</p>
                <p className="text-slate-300 text-sm">31–90 days overdue: {stat(payload.findings.openActionAging.buckets.days31to90)}</p>
                <p className="text-slate-300 text-sm">90+ days overdue: {stat(payload.findings.openActionAging.buckets.days90plus)}</p>
              </div>
            </div>
          )}

          {payload && !error && !fullyEmpty && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Findings by severity</h3>
                <TrendRows rows={payload.findings.bySeverity} linkFor={(k) => auditsLink({ severity: k })} />
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Findings by finding type</h3>
                <TrendRows rows={payload.findings.byFindingType} linkFor={(k) => auditsLink({ findingType: k })} />
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Findings by status</h3>
                <TrendRows rows={payload.findings.byStatus} />
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Top risks (top categories)</h3>
                <ul className="space-y-1 text-sm text-slate-300">
                  {payload.findings.topCategories.map((c) => (
                    <li key={c.key}>
                      {c.key}: {c.count} (previous {c.previous}, {c.delta > 0 ? '+' : ''}{c.delta}, share {c.sharePct}%)
                    </li>
                  ))}
                  {!payload.findings.topCategories.length && (
                    <li className="text-slate-500">No categories recorded for this period.</li>
                  )}
                </ul>
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Incidents by type</h3>
                <TrendRows rows={payload.incidents.byType} linkFor={(k) => incidentsLink({ type: k })} />
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Incidents by severity</h3>
                <TrendRows rows={payload.incidents.bySeverity} linkFor={(k) => incidentsLink({ severity: k })} />
              </div>
              {/* MED-4: incident form-data facets, straight from the payload */}
              {(payload.incidents.facets?.length ? payload.incidents.facets : [{ path: '', values: [] }]).map((facet, fi) => {
                const label = facet.path ? facet.path.split('.').pop()! : '';
                return (
                  <div key={facet.path || `facet-${fi}`} className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                    <h3 className="text-white font-semibold mb-2" title={facet.path || undefined}>
                      Incident facets{label ? ` — ${label}` : ''}
                    </h3>
                    {facet.values.length ? (
                      <TrendRows rows={facet.values} />
                    ) : (
                      <p className="text-slate-500 text-sm">No facet values recorded for this period.</p>
                    )}
                  </div>
                );
              })}
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Audits by contractor</h3>
                <TrendRows rows={payload.leadingIndicators.auditsByContractor} linkFor={(k) => auditsLink({ contractorName: k })} />
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Audits by site</h3>
                <TrendRows rows={payload.leadingIndicators.auditsBySite} linkFor={(k) => auditsLink({ siteName: k })} />
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Audits by rig type</h3>
                <TrendRows rows={payload.leadingIndicators.auditsByRigType} linkFor={(k) => auditsLink({ towerType: k })} />
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Audits by supervisor</h3>
                <TrendRows rows={payload.leadingIndicators.auditsBySupervisor} linkFor={(k) => auditsLink({ constructionSupervisor: k })} />
              </div>
              <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4">
                <h3 className="text-white font-semibold mb-2">Investigation factors (5M categories)</h3>
                <TrendRows rows={payload.leadingIndicators.investigationFactorsByCategory} />
              </div>
            </div>
          )}
          <p className="text-slate-500 text-xs mt-6">
            CSV exports of the raw rows live on the Audits and Incidents tabs (they export the currently loaded rows).
          </p>
          </>)}
        </div>
      )}
    </div>
  );
}

export default HistoryReportsPage;
